import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { Loader } from "@googlemaps/js-api-loader";
import Select from "react-select";
import { Maximize2, Minimize2 } from "lucide-react";
import { tokenStore, salesApiInstance } from "../../api/client.js";
import { usersApi } from "../../api/users.js";

const ALL_OPTION = { value: "ALL", label: "All Sales Persons" };
const NEUTRAL_COLOR = "#6b7280"; // fallback swatch — "All" option, not-yet-colored roster entries

// Assigns a random color to a salesperson the first time they're added to the
// map or picked in the filter, then remembers it so it never changes again.
function getOrAssignColor(colorByIdRef, id) {
    const key = String(id);
    if (!colorByIdRef.current[key]) {
        const hue = Math.floor(Math.random() * 360);
        colorByIdRef.current[key] = `hsl(${hue}, 65%, 45%)`;
    }
    return colorByIdRef.current[key];
}

function escapeXml(str) {
    return String(str).replace(
        /[&<>"']/g,
        (c) =>
            ({
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                '"': "&quot;",
                "'": "&apos;",
            })[c],
    );
}

// Builds a WhatsApp-style "live location" pin — a name tag floating above a
// circular avatar bubble, with a pointer tail whose tip is the actual GPS
// coordinate. Baked into one SVG (rather than a separate label overlay) so
// it can still be used as a plain google.maps.Marker icon, keeping the
// existing position-interpolation code (animateMarkerTo) untouched.
function buildLiveMarkerIcon(name, color) {
    const label = name || "Unknown";
    const truncated = label.length > 18 ? `${label.slice(0, 17)}…` : label;
    const initial = label.trim().charAt(0).toUpperCase() || "?";

    const CHAR_WIDTH = 6.4; // approx glyph width at 11px bold
    const PILL_PAD_X = 10;
    const PILL_HEIGHT = 20;
    const AVATAR_RADIUS = 15;
    const GAP = 4;
    const TAIL_WIDTH = 11;
    const TAIL_HEIGHT = 9;

    const pillWidth = Math.max(
        50,
        Math.round(truncated.length * CHAR_WIDTH) + PILL_PAD_X * 2,
    );
    const width = Math.max(pillWidth, AVATAR_RADIUS * 2 + 10);
    const centerX = width / 2;
    const avatarCenterY = PILL_HEIGHT + GAP + AVATAR_RADIUS;
    const height = avatarCenterY + AVATAR_RADIUS + TAIL_HEIGHT;

    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect x="${centerX - pillWidth / 2}" y="0" width="${pillWidth}" height="${PILL_HEIGHT}" rx="${PILL_HEIGHT / 2}" fill="#111827" fill-opacity="0.9" />
  <text x="${centerX}" y="${PILL_HEIGHT / 2 + 4}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="11" font-weight="700" fill="#ffffff">${escapeXml(truncated)}</text>
  <polygon points="${centerX - TAIL_WIDTH / 2},${avatarCenterY + AVATAR_RADIUS - 2} ${centerX + TAIL_WIDTH / 2},${avatarCenterY + AVATAR_RADIUS - 2} ${centerX},${avatarCenterY + AVATAR_RADIUS + TAIL_HEIGHT}" fill="${color}" />
  <circle cx="${centerX}" cy="${avatarCenterY}" r="${AVATAR_RADIUS}" fill="${color}" stroke="#ffffff" stroke-width="3" />
  <text x="${centerX}" y="${avatarCenterY + 5}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="14" font-weight="700" fill="#ffffff">${escapeXml(initial)}</text>
</svg>`.trim();

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(width, height),
        anchor: new window.google.maps.Point(centerX, height),
    };
}

const isLocal = window.location.hostname === "localhost";
const SOCKET_URL = isLocal
    ? "http://localhost:5001"
    : "https://api.meetelecomm.com";
// In prod, Nginx proxies /sales/socket.io/ -> sales-api's default /socket.io/
// path, so the client must ask for that path explicitly. Locally there's no
// Nginx prefix in the way, so the engine.io default path is used as-is.
const SOCKET_OPTIONS = isLocal ? {} : { path: "/sales/socket.io/" };

const DEFAULT_CENTER = { lat: 22.5726, lng: 88.3639 }; // Kolkata, West Bengal
const DEFAULT_ZOOM = 14;

// Flip to false to re-enable the real map. While true, no Maps API key fetch,
// no SDK load, and no geolocation prompt happen — just an empty placeholder,
// for styling the rest of the page without triggering map loads.
const DISABLE_MAP = false;

// Matches the ~5s cadence at which the server pushes location packets, so a
// marker glides continuously instead of jumping on each update.
const ANIMATION_DURATION_MS = 5000;

// Linearly interpolates a marker from its current position to `to`,
// cancelling any animation already in flight for that marker.
function animateMarkerTo(entry, to, durationMs) {
    if (entry.rafId) cancelAnimationFrame(entry.rafId);

    const current = entry.marker.getPosition();
    const from = { lat: current.lat(), lng: current.lng() };
    const startTime = performance.now();

    const step = (now) => {
        const t = Math.min((now - startTime) / durationMs, 1);
        entry.marker.setPosition({
            lat: from.lat + (to.lat - from.lat) * t,
            lng: from.lng + (to.lng - from.lng) * t,
        });
        entry.rafId = t < 1 ? requestAnimationFrame(step) : null;
    };

    entry.rafId = requestAnimationFrame(step);
}

// yyyy-mm-dd for today, in the browser's local timezone — used as the date
// input's default so "today" means the admin's today, not UTC's.
function todayLocalDateString() {
    const now = new Date();
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now - tzOffsetMs).toISOString().slice(0, 10);
}

/**
 * Live map of field sales executives. Marker positions are mutated directly
 * via refs on every socket packet — no React state/re-render is involved in
 * the hot path, since location updates can arrive many times per second.
 */
export default function LiveTrackerPage({ user }) {
    const rootRef = useRef(null); // fullscreen target — subheader + map together
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef({}); // salespersonId -> { marker, rafId }
    const myMarkerRef = useRef(null); // admin's own device location
    const visibleIdsRef = useRef(null); // null = show everyone; else Set of salespersonId
    const colorByIdRef = useRef({}); // salespersonId -> hex, kept for the socket handler
    const pathPolylineRef = useRef(null); // drawn trail for the path-history overlay
    const pathStopMarkersRef = useRef([]); // is_visit=true markers along that trail

    const activeTabRef = useRef("live"); // mirrors activeTab for the socket handler's closure

    const [isConnected, setIsConnected] = useState(false);
    const [salesPersons, setSalesPersons] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([ALL_OPTION]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [activeTab, setActiveTab] = useState("live"); // "live" | "path" — mutually exclusive views on the same map

    // ─── Path History overlay — replay one rep's day on the same map ────────
    const [pathUserId, setPathUserId] = useState("");
    const [pathDate, setPathDate] = useState(todayLocalDateString());
    const [pathLoading, setPathLoading] = useState(false);
    const [pathError, setPathError] = useState("");
    const [pathActive, setPathActive] = useState(false);
    const [pathDistanceKm, setPathDistanceKm] = useState(null);

    // Keep isFullscreen in sync even when the browser exits fullscreen on its
    // own (e.g. the user presses Esc instead of clicking the toggle button).
    useEffect(() => {
        const handleChange = () =>
            setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", handleChange);
        return () =>
            document.removeEventListener("fullscreenchange", handleChange);
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            rootRef.current?.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
        // The map container's size changes on the next layout pass; nudge
        // Google Maps to re-measure itself once that's settled.
        setTimeout(() => {
            if (window.google && mapInstanceRef.current) {
                window.google.maps.event.trigger(
                    mapInstanceRef.current,
                    "resize",
                );
            }
        }, 100);
    };

    // Load the roster of field sales executives for the filter dropdown.
    // Colors are NOT assigned here — only when a person is added to the map
    // or picked in the filter (see getOrAssignColor).
    useEffect(() => {
        usersApi
            .getAll({ role: "sales", limit: 200 })
            .then((res) => {
                const list = res.data ?? res;
                const roster = Array.isArray(list) ? list : (list.data ?? []);
                setSalesPersons(roster.filter((u) => u.is_active !== false));
            })
            .catch(() => {});
    }, []);

    // Keep a ref of which salesperson ids should be visible, so the socket
    // handler (which never re-renders) can consult it without re-subscribing.
    // Also re-applies whenever the tab switches — the live layer is fully
    // hidden while Path History is active, so the two views never overlap.
    useEffect(() => {
        activeTabRef.current = activeTab;

        const showAll =
            selectedOptions.length === 0 ||
            selectedOptions.some((o) => o.value === "ALL");
        visibleIdsRef.current = showAll
            ? null
            : new Set(selectedOptions.map((o) => String(o.value)));

        const onLiveTab = activeTab === "live";
        Object.entries(markersRef.current).forEach(([id, entry]) => {
            entry.marker.setVisible(
                onLiveTab && (showAll || visibleIdsRef.current.has(String(id))),
            );
        });
        myMarkerRef.current?.setVisible(onLiveTab);

        // Leaving Path History clears its overlay so switching back to it
        // later starts clean rather than showing a stale trail.
        if (onLiveTab) clearPath();
    }, [selectedOptions, activeTab]);

    const handleFilterChange = (selected) => {
        if (!selected || selected.length === 0) {
            setSelectedOptions([]);
            return;
        }
        const pickedAll = selected.some((o) => o.value === "ALL");
        const hadAll = selectedOptions.some((o) => o.value === "ALL");

        const next =
            pickedAll && !hadAll
                ? [ALL_OPTION]
                : selected.filter((o) => o.value !== "ALL");
        next.forEach((o) => getOrAssignColor(colorByIdRef, o.value)); // color assigned on selection
        setSelectedOptions(next);
    };

    // Wipes the currently drawn trail/stop markers, if any. Safe to call
    // whether or not a path is showing.
    const clearPath = () => {
        if (pathPolylineRef.current) {
            pathPolylineRef.current.setMap(null);
            pathPolylineRef.current = null;
        }
        pathStopMarkersRef.current.forEach((marker) => marker.setMap(null));
        pathStopMarkersRef.current = [];
        setPathActive(false);
        setPathError("");
        setPathDistanceKm(null);
    };

    // Fetches one salesperson's recorded points for the chosen day (see
    // GET /api/shift/:userId/path in shiftController.ts) and draws them as a
    // polyline, with a distinct marker at every point flagged is_visit=true
    // (captured when the rep submits "Log Visit" — see visitLogController.ts).
    const handleShowPath = async () => {
        const map = mapInstanceRef.current;
        if (!pathUserId || !map || !window.google) return;

        setPathLoading(true);
        setPathError("");
        try {
            const { data } = await salesApiInstance.get(
                `/shift/${pathUserId}/path`,
                { params: { date: pathDate } },
            );
            const points = data?.points ?? [];

            clearPath();

            if (points.length === 0) {
                setPathError("No recorded locations for that day.");
                return;
            }

            setPathDistanceKm(
                typeof data?.totalDistanceKm === "number"
                    ? data.totalDistanceKm
                    : null,
            );

            const latLngs = points.map((p) => ({
                lat: Number(p.lat),
                lng: Number(p.lng),
            }));

            pathPolylineRef.current = new window.google.maps.Polyline({
                path: latLngs,
                geodesic: true,
                strokeColor: "#2563eb",
                strokeOpacity: 0.85,
                strokeWeight: 4,
                map,
            });

            // One marker per point, not just the visit stops — a plain
            // tracking ping gets a small blue dot; a visit stop gets a
            // bigger orange "V" pin on top. Point order is oldest-first (per
            // the endpoint's ORDER BY recorded_at ASC), so the marker's
            // sequence number doubles as a rough timeline.
            pathStopMarkersRef.current = points.map(
                (p, i) =>
                    new window.google.maps.Marker({
                        position: { lat: Number(p.lat), lng: Number(p.lng) },
                        map,
                        title: `#${i + 1} — ${new Date(p.recorded_at).toLocaleTimeString()}${p.is_visit ? " (visit stop)" : ""}`,
                        zIndex: p.is_visit ? 999 : 500,
                        icon: p.is_visit
                            ? {
                                  path: window.google.maps.SymbolPath.CIRCLE,
                                  scale: 9,
                                  fillColor: "#f97316",
                                  fillOpacity: 1,
                                  strokeColor: "#ffffff",
                                  strokeWeight: 2,
                              }
                            : {
                                  path: window.google.maps.SymbolPath.CIRCLE,
                                  scale: 5,
                                  fillColor: "#2563eb",
                                  fillOpacity: 0.9,
                                  strokeColor: "#ffffff",
                                  strokeWeight: 1,
                              },
                        label: p.is_visit
                            ? {
                                  text: "V",
                                  color: "#ffffff",
                                  fontSize: "10px",
                                  fontWeight: "bold",
                              }
                            : undefined,
                    }),
            );

            const bounds = new window.google.maps.LatLngBounds();
            latLngs.forEach((p) => bounds.extend(p));
            map.fitBounds(bounds);

            setPathActive(true);
        } catch (err) {
            console.error("[live-tracker] failed to load path:", err);
            setPathError(err?.response?.data?.error || "Failed to load path.");
        } finally {
            setPathLoading(false);
        }
    };

    // Instantiate the map exactly once. mapContainerRef never changes identity,
    // so the canvas element is never torn down or recreated. The Maps API key
    // is fetched from the server at runtime rather than baked into the bundle.
    useEffect(() => {
        if (DISABLE_MAP) return;
        let cancelled = false;

        salesApiInstance.get("/config/maps-key").then(({ data }) => {
            if (cancelled || !data.apiKey) return;

            const loader = new Loader({
                apiKey: data.apiKey,
                version: "weekly",
            });

            loader.load().then((google) => {
                if (
                    cancelled ||
                    !mapContainerRef.current ||
                    mapInstanceRef.current
                )
                    return;
                mapInstanceRef.current = new google.maps.Map(
                    mapContainerRef.current,
                    {
                        center: DEFAULT_CENTER,
                        zoom: DEFAULT_ZOOM,
                        disableDefaultUI: true,
                        zoomControl: true,
                    },
                );

                const placeMyMarker = (position) => {
                    if (cancelled || !mapInstanceRef.current) return;
                    myMarkerRef.current = new google.maps.Marker({
                        position,
                        map: mapInstanceRef.current,
                        title: user?.name || "You",
                        icon: buildLiveMarkerIcon(
                            user?.name || "You",
                            "#2563eb",
                        ),
                        zIndex: 500,
                    });
                };

                if (!navigator.geolocation) {
                    placeMyMarker(DEFAULT_CENTER);
                    return;
                }
                navigator.geolocation.getCurrentPosition(
                    ({ coords }) =>
                        placeMyMarker({
                            lat: coords.latitude,
                            lng: coords.longitude,
                        }),
                    () => placeMyMarker(DEFAULT_CENTER), // permission denied / unavailable
                );
            });
        });

        return () => {
            cancelled = true;
            if (myMarkerRef.current) myMarkerRef.current.setMap(null);
        };
    }, []);

    // Socket lifecycle, kept independent from map-load timing.
    useEffect(() => {
        const socket = io(SOCKET_URL, {
            ...SOCKET_OPTIONS,
            auth: { token: tokenStore.get() },
            transports: ["websocket", "polling"],
        });

        socket.on("connect", () => {
            console.log("[live-tracker] connected:", socket.id);
            setIsConnected(true);
        });
        socket.on("disconnect", (reason) => {
            console.log("[live-tracker] disconnected:", reason);
            setIsConnected(false);
        });

        // Matches the event name the server actually emits in
        // server/packages/sales-api/src/sockets/liveTracker.ts.
        socket.on("salesperson_moved", ({ salespersonId, lat, lng, name }) => {
            console.log(
                `[live-tracker] salesperson_moved: ${name || salespersonId} -> lat ${lat}, lng ${lng}`,
            );

            const map = mapInstanceRef.current;
            if (!map || !window.google) return;

            const position = { lat, lng };
            const entry = markersRef.current[salespersonId];

            if (entry) {
                animateMarkerTo(entry, position, ANIMATION_DURATION_MS);
            } else {
                const visible =
                    activeTabRef.current === "live" &&
                    (visibleIdsRef.current === null ||
                        visibleIdsRef.current.has(String(salespersonId)));
                markersRef.current[salespersonId] = {
                    marker: new window.google.maps.Marker({
                        position,
                        map,
                        title: name || salespersonId,
                        visible,
                        icon: buildLiveMarkerIcon(
                            name || `#${salespersonId}`,
                            getOrAssignColor(colorByIdRef, salespersonId),
                        ),
                        zIndex: 500,
                    }),
                    rafId: null,
                };
            }
        });

        // Emitted the instant a rep checks out (see notifyShiftEnded in
        // sockets/liveTracker.ts) — remove their marker immediately rather
        // than leaving a stale dot frozen on the map.
        socket.on("salesperson_offline", ({ salespersonId }) => {
            console.log(`[live-tracker] salesperson_offline: ${salespersonId}`);
            const entry = markersRef.current[salespersonId];
            if (!entry) return;
            if (entry.rafId) cancelAnimationFrame(entry.rafId);
            entry.marker.setMap(null);
            delete markersRef.current[salespersonId];
        });

        return () => {
            socket.off("connect");
            socket.off("disconnect");
            socket.off("salesperson_moved");
            socket.off("salesperson_offline");
            socket.disconnect();

            Object.values(markersRef.current).forEach(({ marker, rafId }) => {
                if (rafId) cancelAnimationFrame(rafId);
                marker.setMap(null);
            });
            markersRef.current = {};
        };
    }, []);

    // Path-history overlay cleanup on unmount.
    useEffect(() => {
        return () => {
            if (pathPolylineRef.current) pathPolylineRef.current.setMap(null);
            pathStopMarkersRef.current.forEach((marker) => marker.setMap(null));
        };
    }, []);

    return (
        <div
            ref={rootRef}
            style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: isFullscreen ? "100vh" : "calc(100vh - 64px)",
                background: "#fff",
            }}
        >
            {/* Tab switcher — Live Tracker and Path History are mutually exclusive
                views of the same map; switching tabs hides the other view entirely
                instead of layering both on top of each other. */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0 16px",
                    borderBottom: "1px solid #e5e7eb",
                    flexShrink: 0,
                }}
            >
                {[
                    { key: "live", label: "Live Tracker" },
                    { key: "path", label: "Path History" },
                ].map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            padding: "12px 16px",
                            border: "none",
                            borderBottom:
                                activeTab === tab.key
                                    ? "2px solid #2563eb"
                                    : "2px solid transparent",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            color:
                                activeTab === tab.key ? "#2563eb" : "#6b7280",
                        }}
                    >
                        {tab.label}
                    </button>
                ))}

                <button
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "Exit full screen" : "Full screen"}
                    style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                    }}
                >
                    {isFullscreen ? (
                        <Minimize2 size={15} />
                    ) : (
                        <Maximize2 size={15} />
                    )}
                    {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </button>
            </div>

            {activeTab === "live" && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 16px",
                        borderBottom: "1px solid #e5e7eb",
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#374151",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Show Sales Persons
                    </span>
                    <div style={{ width: 320 }}>
                        <Select
                            isMulti
                            options={[
                                ALL_OPTION,
                                ...salesPersons.map((p) => ({
                                    value: String(p.id),
                                    label: p.name,
                                })),
                            ]}
                            value={selectedOptions}
                            onChange={handleFilterChange}
                            closeMenuOnSelect={false}
                            placeholder="Select sales persons..."
                            formatOptionLabel={(option) => (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            flexShrink: 0,
                                            background:
                                                option.value === "ALL"
                                                    ? NEUTRAL_COLOR
                                                    : colorByIdRef.current[
                                                          option.value
                                                      ] || NEUTRAL_COLOR,
                                        }}
                                    />
                                    {option.label}
                                </div>
                            )}
                        />
                    </div>
                </div>
            )}

            {activeTab === "path" && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                        padding: "10px 16px",
                        borderBottom: "1px solid #e5e7eb",
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#374151",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Path History
                    </span>
                    <div style={{ width: 220 }}>
                        <Select
                            options={salesPersons.map((p) => ({
                                value: String(p.id),
                                label: p.name,
                            }))}
                            value={
                                salesPersons
                                    .map((p) => ({
                                        value: String(p.id),
                                        label: p.name,
                                    }))
                                    .find((o) => o.value === pathUserId) || null
                            }
                            onChange={(option) =>
                                setPathUserId(option?.value || "")
                            }
                            placeholder="Select sales person..."
                            isClearable
                        />
                    </div>
                    <input
                        type="date"
                        value={pathDate}
                        max={todayLocalDateString()}
                        onChange={(e) => setPathDate(e.target.value)}
                        style={{
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #d1d5db",
                            fontSize: 13,
                            color: "#374151",
                        }}
                    />
                    <button
                        onClick={handleShowPath}
                        disabled={!pathUserId || pathLoading}
                        style={{
                            padding: "8px 14px",
                            borderRadius: 8,
                            border: "none",
                            background:
                                !pathUserId || pathLoading
                                    ? "#93c5fd"
                                    : "#2563eb",
                            color: "#fff",
                            cursor:
                                !pathUserId || pathLoading
                                    ? "default"
                                    : "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {pathLoading ? "Loading..." : "Show Path"}
                    </button>
                    {pathActive && (
                        <button
                            onClick={clearPath}
                            style={{
                                padding: "8px 14px",
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                        >
                            Clear Path
                        </button>
                    )}
                    {pathError && (
                        <span style={{ fontSize: 12, color: "#dc2626" }}>
                            {pathError}
                        </span>
                    )}
                    {pathActive && !pathError && pathDistanceKm !== null && (
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#fff",
                                background: "#2563eb",
                                padding: "4px 12px",
                                borderRadius: 20,
                            }}
                        >
                            Total Distance: {pathDistanceKm} km
                        </span>
                    )}
                    {pathActive && !pathError && (
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                                color: "#6b7280",
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: "#f97316",
                                    display: "inline-block",
                                }}
                            />
                            = visit stop
                        </span>
                    )}
                </div>
            )}

            {/* Map area */}
            <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
                <div
                    ref={mapContainerRef}
                    style={{
                        width: "100%",
                        height: "100%",
                        background: DISABLE_MAP ? "#e5e7eb" : undefined,
                    }}
                />

                {activeTab === "live" && (
                    <div
                        style={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 14px",
                            borderRadius: 8,
                            background: "rgba(255,255,255,0.95)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: isConnected ? "#22c55e" : "#ef4444",
                                display: "inline-block",
                            }}
                        />
                        <span style={{ color: "#1f2937" }}>
                            {isConnected ? "Connected" : "Disconnected"}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
